import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import { isAxiosError } from 'axios'
import { apiAxios } from '@/api'
import { fetchWrapper } from '@/reducers/fetch'
import { handleApiError } from '@/utils/errorHelpers'
import type { Dispatch, GetState } from '@/utils/typeHelpers'
import {
  selectCatalogRequestId,
  selectPlanOptionsFetchedAt,
} from './planChange.selectors'
import type {
  AppliedPlanChange,
  CatalogLookupKey,
  PlanCatalog,
  PlanChangeRequest,
  PlanOptions,
  PromoCodePreview,
} from './planChange.types'

type CatalogRequestStatus = 'idle' | 'loading' | 'failed'

interface PlanChangeState {
  planOptions: PlanOptions | null
  fetchedAt: number | null
  lastAppliedChange: AppliedPlanChange | null
  planCatalog: PlanCatalog | null
  catalogRequest: { id: number; status: CatalogRequestStatus }
}

const initialState: PlanChangeState = {
  planOptions: null,
  fetchedAt: null,
  lastAppliedChange: null,
  planCatalog: null,
  catalogRequest: { id: 0, status: 'idle' },
}

const planChangeSlice = createSlice({
  name: 'planChange',
  initialState,
  reducers: {
    receivePlanOptions: (
      state,
      action: PayloadAction<{ options: PlanOptions; fetchedAt: number }>
    ) => {
      state.planOptions = action.payload.options
      state.fetchedAt = action.payload.fetchedAt
      state.planCatalog = null
    },
    receiveAppliedChange: (state, action: PayloadAction<AppliedPlanChange>) => {
      state.lastAppliedChange = action.payload
    },
    clearAppliedChange: (state) => {
      state.lastAppliedChange = null
    },
    catalogRequested: (state, action: PayloadAction<{ id: number }>) => {
      state.catalogRequest = { id: action.payload.id, status: 'loading' }
    },
    catalogReceived: (
      state,
      action: PayloadAction<{
        id: number
        catalog: PlanCatalog
        forFetchedAt: number | null
      }>
    ) => {
      if (action.payload.id !== state.catalogRequest.id) return
      // A reply for older options must not overwrite the lineup built for the current ones.
      if (action.payload.forFetchedAt !== state.fetchedAt) return
      state.planCatalog = action.payload.catalog
      state.catalogRequest.status = 'idle'
    },
    catalogFailed: (state, action: PayloadAction<{ id: number }>) => {
      if (action.payload.id !== state.catalogRequest.id) return
      state.catalogRequest.status = 'failed'
    },
  },
})

export const {
  receivePlanOptions,
  receiveAppliedChange,
  clearAppliedChange,
  catalogRequested,
  catalogReceived,
  catalogFailed,
} = planChangeSlice.actions
export default planChangeSlice.reducer

export const FETCH_PLAN_OPTIONS_KEY = 'FETCH_PLAN_OPTIONS_KEY'
export const fetchPlanOptions = () =>
  fetchWrapper({
    fetchKey: FETCH_PLAN_OPTIONS_KEY,
    fetchFunction: async (dispatch) => {
      const res = await apiAxios.get<PlanOptions>(
        '/finances/api/v2/plan_changes/options'
      )
      dispatch(receivePlanOptions({ options: res.data, fetchedAt: Date.now() }))
      // Catalog `offered` is per-customer, so it is refetched whenever options change.
      dispatch(fetchPlanCatalog())
      return res.data
    },
  })

/**
 * The lineup is the customer's current tier plus the tiers offered to them, so it is per
 * customer. It is refetched whenever the plan options are, so the two stay in step. Each call
 * takes the next request id and only the latest id may change the slice, so a slow older
 * request can neither overwrite nor clear what a newer one reported.
 */
export const fetchPlanCatalog =
  () =>
  async (dispatch: Dispatch, getState: GetState): Promise<void> => {
    const id = selectCatalogRequestId(getState()) + 1
    const forFetchedAt = selectPlanOptionsFetchedAt(getState())
    dispatch(catalogRequested({ id }))
    try {
      const res = await apiAxios.get<PlanCatalog>(
        '/finances/api/v2/plan_changes/catalog'
      )
      dispatch(catalogReceived({ id, catalog: res.data, forFetchedAt }))
    } catch (error) {
      dispatch(catalogFailed({ id }))
      dispatch(handleApiError(error))
    }
  }

export const SUBMIT_PLAN_CHANGE_KEY = 'SUBMIT_PLAN_CHANGE_KEY'
export const submitPlanChange = (body: PlanChangeRequest) =>
  fetchWrapper({
    fetchKey: SUBMIT_PLAN_CHANGE_KEY,
    fetchFunction: async (dispatch) => {
      const res = await apiAxios.post<AppliedPlanChange>(
        '/finances/api/v2/plan_changes',
        body
      )
      dispatch(receiveAppliedChange(res.data))
      return res.data
    },
  })

const PREVIEW_PROMO_CODE_KEY = 'PREVIEW_PROMO_CODE_KEY'
/** Never stored in redux: local to whichever confirm screen is asking. */
export const previewPromoCode = (body: {
  code: string
  toLookupKey: CatalogLookupKey
}) =>
  fetchWrapper({
    fetchKey: PREVIEW_PROMO_CODE_KEY,
    fetchFunction: async () => {
      const res = await apiAxios.post<PromoCodePreview>(
        '/finances/api/v2/plan_changes/promo_code',
        body
      )
      return res.data
    },
  })

export const CANCEL_PENDING_PLAN_CHANGE_KEY = 'CANCEL_PENDING_PLAN_CHANGE_KEY'
export const cancelPendingPlanChange = () =>
  fetchWrapper({
    fetchKey: CANCEL_PENDING_PLAN_CHANGE_KEY,
    fetchFunction: async () => {
      try {
        await apiAxios.delete('/finances/api/v2/plan_changes/pending')
        return true
      } catch (error) {
        // 404 = nothing we parked is pending (double-click, another tab, or a
        // support-owned schedule). Success by contract, not an error.
        if (isAxiosError(error) && error.response?.status === 404) {
          return true
        }
        throw error
      }
    },
  })
